/**
 * Combobox — the searchable listbox.
 *
 * Radix Popover for placement and dismissal, everything else hand-built: no cmdk,
 * because we need grouped rows, description lines, multi-select and a virtualised
 * body past 100 options in one keyboard model.
 *
 * Focus is deliberately *not* trapped (ARIA combobox pattern): Esc closes and
 * restores, Tab closes and moves on, and the list is driven by
 * `aria-activedescendant` while the caret stays in the filter field.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ControlSize } from './Button';
import { FIELD_PAD, fieldShell } from './Input';
import type { SelectOption } from './Select';
import { Skeleton } from './Skeleton';

/* ── Shared listbox vocabulary (Select renders the same objects) ──────────── */

export const LISTBOX_SURFACE = [
  'z-[var(--z-popover)] overflow-hidden rounded-md',
  'border border-line bg-surface-overlay shadow-3',
].join(' ');

/* `rounded-xs!` and `outline-none!` beat base.css's unlayered `:focus-visible`,
   which would otherwise re-round the row to `--radius-sm` the moment it takes
   focus — visible as a corner twitch on the highlight. */
export const OPTION_ROW = [
  'relative flex w-full cursor-default items-center gap-2 rounded-xs!',
  'pl-2 pr-7 text-base text-primary select-none',
  'outline-none focus-visible:outline-none!',
  'transition-colors duration-[var(--dur-instant)] ease-[var(--ease-out)]',
].join(' ');

/* `.eyebrow` ships at `--text-faint` (≈3:1). These labels name actionable
   groups, so they get lifted to `--text-muted` — important, because `.eyebrow`
   is unlayered and would otherwise win. */
export const GROUP_LABEL = 'eyebrow flex items-end px-2 pb-1 text-muted!';

/** Bucket options by `group`, preserving first-seen order. */
export function groupOptions(options: SelectOption[]): Array<[string, SelectOption[]]> {
  const buckets = new Map<string, SelectOption[]>();
  for (const option of options) {
    const key = option.group ?? '';
    const bucket = buckets.get(key);
    if (bucket) bucket.push(option);
    else buckets.set(key, [option]);
  }
  return [...buckets.entries()];
}

/* ── Row model ────────────────────────────────────────────────────────────── */

type Row =
  | { kind: 'group'; key: string; label: string }
  | { kind: 'option'; key: string; option: SelectOption };

/** Fixed row heights keep the virtualiser pixel-exact — no measurement pass. */
const ROW_H = 28;
const ROW_H_DESC = 40;
const VIRTUALIZE_THRESHOLD = 100;
const SEARCH_DEBOUNCE_MS = 180;
const PAGE_JUMP = 8;

function rowHeight(row: Row | undefined): number {
  if (!row) return ROW_H;
  return row.kind === 'option' && row.option.description ? ROW_H_DESC : ROW_H;
}

export interface ComboboxProps {
  options: SelectOption[];
  /** A single value, or the selected set when `multiple`. */
  value?: string | string[] | null;
  /** Single-select callback. */
  onValueChange?: (value: string) => void;
  /** Multi-select callback — required when `multiple` is set. */
  onValuesChange?: (values: string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  /** Show skeleton rows while the caller refills `options`. */
  loading?: boolean;
  /** Hand filtering to the server. Debounced; local filtering is switched off. */
  onSearch?: (query: string) => void | Promise<void>;
  renderOption?: (
    option: SelectOption,
    state: { selected: boolean; active: boolean },
  ) => ReactNode;
  clearable?: boolean;
  /** Pinned below the list — "Create tag", counts, hints. */
  footer?: ReactNode;
  className?: string;
  contentClassName?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /**
   * Passed straight through to the trigger. A combobox that blocks a form's
   * submit owes the reader the same declaration a required `<input>` makes;
   * without it the field is indistinguishable from the optional controls beside
   * it and the disabled submit states no reason.
   */
  'aria-required'?: boolean;
}

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(
  {
    options,
    value,
    onValueChange,
    onValuesChange,
    multiple = false,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyMessage = 'No matches',
    size = 'md',
    invalid = false,
    disabled = false,
    loading = false,
    onSearch,
    renderOption,
    clearable = false,
    footer,
    className,
    contentClassName,
    align = 'start',
    side = 'bottom',
    id,
    ...aria
  },
  ref,
) {
  const reactId = useId();
  const listId = `${reactId}-list`;
  const inputId = id ?? `${reactId}-input`;
  const optionDomId = useCallback(
    (optionValue: string) => `${reactId}-opt-${encodeURIComponent(optionValue)}`,
    [reactId],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeValue, setActiveValue] = useState<string | null>(null);

  const selectedValues = useMemo<string[]>(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );
  const selectedOptions = useMemo(
    () => options.filter((option) => selectedValues.includes(option.value)),
    [options, selectedValues],
  );

  const commit = useCallback(
    (next: string[]) => {
      if (multiple) onValuesChange?.(next);
      else onValueChange?.(next[0] ?? '');
    },
    [multiple, onValueChange, onValuesChange],
  );

  /* Filtering ------------------------------------------------------------- */
  const serverFiltered = Boolean(onSearch);
  const filtered = useMemo(() => {
    if (serverFiltered) return options;
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().includes(needle) ||
        (option.description?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, query, serverFiltered]);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const [group, list] of groupOptions(filtered)) {
      if (group) out.push({ kind: 'group', key: `group:${group}`, label: group });
      for (const option of list) out.push({ kind: 'option', key: option.value, option });
    }
    return out;
  }, [filtered]);

  /** Indices of the rows a keyboard user can land on. */
  const stops = useMemo(() => {
    const out: number[] = [];
    rows.forEach((row, index) => {
      if (row.kind === 'option' && !row.option.disabled) out.push(index);
    });
    return out;
  }, [rows]);

  const activeIndex = useMemo(() => {
    const found = rows.findIndex(
      (row) => row.kind === 'option' && row.option.value === activeValue,
    );
    return found >= 0 ? found : (stops[0] ?? -1);
  }, [rows, activeValue, stops]);

  const moveTo = useCallback(
    (stopPosition: number) => {
      if (stops.length === 0) return;
      const clamped = Math.max(0, Math.min(stops.length - 1, stopPosition));
      const rowIndex = stops[clamped];
      const row = rowIndex === undefined ? undefined : rows[rowIndex];
      if (row?.kind === 'option') setActiveValue(row.option.value);
    },
    [rows, stops],
  );

  const move = useCallback(
    (delta: number) => {
      if (stops.length === 0) return;
      const at = stops.indexOf(activeIndex);
      const from = at >= 0 ? at : 0;
      const next = at < 0 ? 0 : from + delta;
      moveTo(next < 0 ? stops.length - 1 : next > stops.length - 1 ? 0 : next);
    },
    [activeIndex, moveTo, stops],
  );

  /* Async search ---------------------------------------------------------- */
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  });
  useEffect(() => {
    if (!onSearchRef.current || !open) return;
    const handle = setTimeout(() => void onSearchRef.current?.(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, open]);

  /* Open / close ---------------------------------------------------------- */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setQuery('');
        setActiveValue(selectedValues[0] ?? null);
      }
    },
    [selectedValues],
  );

  const pick = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;
      if (multiple) {
        commit(
          selectedValues.includes(option.value)
            ? selectedValues.filter((v) => v !== option.value)
            : [...selectedValues, option.value],
        );
        setActiveValue(option.value);
      } else {
        commit([option.value]);
        setOpen(false);
      }
    },
    [commit, multiple, selectedValues],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          move(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          move(-1);
          break;
        case 'PageDown':
          event.preventDefault();
          move(PAGE_JUMP);
          break;
        case 'PageUp':
          event.preventDefault();
          move(-PAGE_JUMP);
          break;
        case 'Home':
          event.preventDefault();
          moveTo(0);
          break;
        case 'End':
          event.preventDefault();
          moveTo(stops.length - 1);
          break;
        case 'Enter': {
          const row = activeIndex >= 0 ? rows[activeIndex] : undefined;
          if (row?.kind === 'option') {
            event.preventDefault();
            pick(row.option);
          }
          break;
        }
        case 'Tab':
          setOpen(false);
          break;
        case 'Backspace':
          if (multiple && query === '' && selectedValues.length > 0) {
            event.preventDefault();
            commit(selectedValues.slice(0, -1));
          }
          break;
        default:
          break;
      }
    },
    [activeIndex, commit, move, moveTo, multiple, pick, query, rows, selectedValues, stops.length],
  );

  const clearSelection = useCallback(() => commit([]), [commit]);

  /** The row the caret is "on" — resolved from the index so the first option is
   *  highlighted on open even before the user has moved. */
  const activeRow = activeIndex >= 0 ? rows[activeIndex] : undefined;
  const activeOptionValue = activeRow?.kind === 'option' ? activeRow.option.value : null;

  return (
    <RPopover.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <RPopover.Trigger asChild>
        <button
          ref={ref}
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          onKeyDown={(event) => {
            // Clearing has to be reachable without a pointer: the affordance in
            // the trigger cannot be a focusable element (a button inside a
            // button is invalid), so the key does the work.
            if (
              clearable &&
              selectedValues.length > 0 &&
              (event.key === 'Backspace' || event.key === 'Delete')
            ) {
              event.preventDefault();
              clearSelection();
            }
          }}
          className={cn(
            'group',
            fieldShell({ size, invalid, disabled, focus: 'visible' }),
            FIELD_PAD[size],
            'justify-between text-left',
            'data-[state=open]:border-focus data-[state=open]:shadow-[inset_0_0_0_1px_var(--focus)]',
            className,
          )}
          {...aria}
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            {selectedOptions.length === 0 ? (
              <span className="truncate text-muted">{placeholder}</span>
            ) : multiple ? (
              <>
                {selectedOptions.slice(0, 2).map((option) => (
                  <span
                    key={option.value}
                    className="max-w-36 shrink truncate rounded-xs bg-hover px-1 py-px text-sm text-secondary"
                  >
                    {option.label}
                  </span>
                ))}
                {selectedOptions.length > 2 ? (
                  <span className="shrink-0 text-sm text-muted tnum">
                    +{selectedOptions.length - 2}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                {selectedOptions[0]?.icon ? (
                  <span className="flex shrink-0 items-center text-muted [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]">
                    {selectedOptions[0].icon}
                  </span>
                ) : null}
                <span className="truncate">{selectedOptions[0]?.label}</span>
              </>
            )}
          </span>

          {clearable && selectedOptions.length > 0 ? (
            <span
              aria-hidden="true"
              title="Clear selection (Backspace)"
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                clearSelection();
              }}
              className="grid size-4 shrink-0 place-items-center rounded-xs text-muted hover:bg-hover hover:text-primary"
            >
              <X className="size-3" />
            </span>
          ) : null}

          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-3.5 shrink-0 text-muted',
              'transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              'group-data-[state=open]:rotate-180',
            )}
          />
        </button>
      </RPopover.Trigger>

      <RPopover.Portal>
        <RPopover.Content
          align={align}
          side={side}
          sideOffset={4}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            LISTBOX_SURFACE,
            'flex w-[var(--radix-popover-trigger-width)] min-w-52 flex-col',
            contentClassName,
          )}
          style={{ animation: 'af-scale-in var(--dur-fast) var(--ease-out)' }}
        >
          <div className="flex h-8 shrink-0 items-center gap-1.5 border-b border-line-subtle px-2">
            <Search aria-hidden="true" className="size-3.5 shrink-0 text-muted" />
            <input
              ref={inputRef}
              id={inputId}
              role="combobox"
              type="text"
              autoComplete="off"
              spellCheck={false}
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                activeOptionValue ? optionDomId(activeOptionValue) : undefined
              }
              value={query}
              placeholder={searchPlaceholder}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              className="h-full w-full bg-transparent text-base placeholder:text-muted focus-visible:outline-none!"
            />
          </div>

          <ComboboxList
            listId={listId}
            rows={rows}
            loading={loading}
            emptyMessage={emptyMessage}
            activeValue={activeOptionValue}
            activeIndex={activeIndex}
            selectedValues={selectedValues}
            optionDomId={optionDomId}
            onPick={pick}
            onActivate={setActiveValue}
            renderOption={renderOption}
          />

          {footer ? (
            <div className="shrink-0 border-t border-line-subtle p-1">{footer}</div>
          ) : null}
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
});

/* ── List body ────────────────────────────────────────────────────────────
   A child component so the virtualiser is created against a mounted scroll
   container — Radix unmounts popover content when closed. */

interface ComboboxListProps {
  listId: string;
  rows: Row[];
  loading: boolean;
  emptyMessage: string;
  activeValue: string | null;
  activeIndex: number;
  selectedValues: string[];
  optionDomId: (value: string) => string;
  onPick: (option: SelectOption) => void;
  onActivate: (value: string) => void;
  renderOption?: (
    option: SelectOption,
    state: { selected: boolean; active: boolean },
  ) => ReactNode;
}

function ComboboxList({
  listId,
  rows,
  loading,
  emptyMessage,
  activeValue,
  activeIndex,
  selectedValues,
  optionDomId,
  onPick,
  onActivate,
  renderOption,
}: ComboboxListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualize = rows.length > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => rowHeight(rows[index]),
    overscan: 8,
    enabled: virtualize,
  });

  const scrollToIndex = virtualizer.scrollToIndex;
  useEffect(() => {
    if (activeIndex < 0) return;
    if (virtualize) {
      scrollToIndex(activeIndex, { align: 'auto' });
      return;
    }
    const node = scrollRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, scrollToIndex, virtualize]);

  const renderRow = (row: Row) => {
    if (row.kind === 'group') {
      return (
        <div
          key={row.key}
          role="presentation"
          className={cn(GROUP_LABEL, 'h-7')}
          style={{ height: ROW_H }}
        >
          {row.label}
        </div>
      );
    }

    const option = row.option;
    const selected = selectedValues.includes(option.value);
    const active = option.value === activeValue;

    return (
      <div
        key={row.key}
        id={optionDomId(option.value)}
        role="option"
        aria-selected={selected}
        aria-disabled={option.disabled || undefined}
        data-active={active ? 'true' : undefined}
        onPointerMove={() => {
          if (!option.disabled && !active) onActivate(option.value);
        }}
        // Keep the caret in the filter field: `aria-activedescendant` only works
        // while the input holds focus, and multi-select stays open after a pick.
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => onPick(option)}
        style={{ height: option.description ? ROW_H_DESC : ROW_H }}
        className={cn(
          OPTION_ROW,
          active && 'bg-hover',
          option.disabled && 'pointer-events-none text-muted',
        )}
      >
        {renderOption ? (
          renderOption(option, { selected, active })
        ) : (
          <>
            {option.icon ? (
              <span className="flex shrink-0 items-center text-muted [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]">
                {option.icon}
              </span>
            ) : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate">{option.label}</span>
              {option.description ? (
                <span className="mt-px block truncate text-xs text-muted">
                  {option.description}
                </span>
              ) : null}
            </span>
          </>
        )}
        {selected ? (
          <span className="absolute right-2 flex items-center">
            <Check aria-hidden="true" className="size-3.5 text-accent" strokeWidth={2.5} />
          </span>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div id={listId} role="listbox" aria-busy="true" className="flex flex-col gap-1 p-1">
        {[68, 84, 56, 76, 62, 80].map((width, index) => (
          <div key={index} className="flex h-7 items-center px-2">
            <Skeleton w={`${width}%`} h={10} />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        id={listId}
        role="listbox"
        className="flex h-24 flex-col items-center justify-center gap-1.5 px-4 text-center"
      >
        <Search aria-hidden="true" className="size-4 text-muted" />
        <p className="text-sm text-muted">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      id={listId}
      role="listbox"
      className="max-h-64 min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
    >
      {virtualize ? (
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index];
            if (!row) return null;
            return (
              <div
                key={row.key}
                // `presentation` keeps the listbox → option ownership intact
                // through the virtualiser's positioning wrappers.
                role="presentation"
                className="absolute inset-x-0 top-0"
                style={{ transform: `translateY(${item.start}px)` }}
              >
                {renderRow(row)}
              </div>
            );
          })}
        </div>
      ) : (
        rows.map(renderRow)
      )}
    </div>
  );
}
